import { useForm } from "react-hook-form";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
} from "@heroui/react";
import { api } from "@/api";
import useErrorHandling from "@/hooks/useErrorHandling";
import { toast } from "react-toastify";

interface CianDraftModalProps {
  isOpen: boolean;
  onClose: () => void;
  officeId: number;
}

interface CianDraftForm {
  agent_fee: number;
  client_fee: number;
  deposit: number;
  floors_count: number;
  min_lease_term: number;
  prepay_months: number;
}

export function CianDraftModal({
  isOpen,
  onClose,
  officeId,
}: CianDraftModalProps) {
  const handleError = useErrorHandling();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setError: setFormError,
    clearErrors,
  } = useForm<CianDraftForm>({
    defaultValues: {
      agent_fee: 0,
      client_fee: 0,
      deposit: 0,
      floors_count: 1,
      min_lease_term: 1,
      prepay_months: 1,
    },
  });

  const onSubmit = async (data: CianDraftForm) => {
    try {
      clearErrors();

      const draftData = {
        ...data,
        office_id: officeId,
      };

      await api.cian.createDraft(draftData);
      onClose();
      toast.success("Черновик объявления успешно создан");
    } catch (error: any) {
      const { parsedErrors, errorMessage } = handleError(error);
      toast.error(errorMessage);

      if (parsedErrors && parsedErrors.length > 0) {
        parsedErrors.forEach(({ field, message }) => {
          setFormError(field as keyof CianDraftForm, {
            type: "server",
            message,
          });
        });
      } else {
        setFormError("root", {
          type: "server",
          message: errorMessage,
        });
      }
    }
  };

  const handleClose = () => {
    reset();
    clearErrors();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="2xl">
      <ModalContent>
        <ModalHeader>Создание черновика на Циан</ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit(onSubmit)}>
            <p className="text-sm text-gray-600 space-y-4 mb-4">
              Черновик будет создан в вашем аккаунте на Циан.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Input
                  label="Комиссия агента (%)"
                  type="number"
                  placeholder="0"
                  {...register("agent_fee", {
                    required: "Обязательное поле",
                    min: { value: 0, message: "Минимальное значение: 0" },
                    max: { value: 100, message: "Максимальное значение: 100" },
                    valueAsNumber: true,
                  })}
                  isRequired
                  variant="bordered"
                  min="0"
                  max="100"
                  step="0.1"
                  isInvalid={!!errors.agent_fee}
                  errorMessage={errors.agent_fee?.message}
                />
              </div>

              <div className="space-y-2">
                <Input
                  label="Комиссия клиента (%)"
                  type="number"
                  placeholder="0"
                  {...register("client_fee", {
                    required: "Обязательное поле",
                    min: { value: 0, message: "Минимальное значение: 0" },
                    max: { value: 100, message: "Максимальное значение: 100" },
                    valueAsNumber: true,
                  })}
                  isRequired
                  variant="bordered"
                  min="0"
                  max="100"
                  step="0.1"
                  isInvalid={!!errors.client_fee}
                  errorMessage={errors.client_fee?.message}
                />
              </div>

              <div className="space-y-2">
                <Input
                  label="Депозит (месяцев)"
                  type="number"
                  placeholder="1"
                  {...register("deposit", {
                    required: "Обязательное поле",
                    min: { value: 0, message: "Минимальное значение: 0" },
                    valueAsNumber: true,
                  })}
                  isRequired
                  variant="bordered"
                  min="0"
                  step="0.5"
                  isInvalid={!!errors.deposit}
                  errorMessage={errors.deposit?.message}
                />
              </div>

              <div className="space-y-2">
                <Input
                  label="Количество этажей"
                  type="number"
                  placeholder="1"
                  {...register("floors_count", {
                    required: "Обязательное поле",
                    min: { value: 1, message: "Минимальное значение: 1" },
                    valueAsNumber: true,
                  })}
                  isRequired
                  variant="bordered"
                  min="1"
                  isInvalid={!!errors.floors_count}
                  errorMessage={errors.floors_count?.message}
                />
              </div>

              <div className="space-y-2">
                <Input
                  label="Минимальный срок аренды (месяцев)"
                  type="number"
                  placeholder="1"
                  {...register("min_lease_term", {
                    required: "Обязательное поле",
                    min: { value: 1, message: "Минимальное значение: 1" },
                    valueAsNumber: true,
                  })}
                  isRequired
                  variant="bordered"
                  min="1"
                  isInvalid={!!errors.min_lease_term}
                  errorMessage={errors.min_lease_term?.message}
                />
              </div>

              <div className="space-y-2">
                <Input
                  label="Предоплата (месяцев)"
                  type="number"
                  placeholder="1"
                  {...register("prepay_months", {
                    required: "Обязательное поле",
                    min: { value: 1, message: "Минимальное значение: 1" },
                    valueAsNumber: true,
                  })}
                  isRequired
                  variant="bordered"
                  min="1"
                  isInvalid={!!errors.prepay_months}
                  errorMessage={errors.prepay_months?.message}
                />
              </div>
            </div>

            {errors.root && (
              <div className="text-red-500 text-sm">{errors.root.message}</div>
            )}
          </form>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" variant="light" onPress={handleClose}>
            Отмена
          </Button>
          <Button
            color="primary"
            onPress={() => handleSubmit(onSubmit)()}
            isLoading={isSubmitting}
          >
            Создать черновик
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
